import React, { useState } from "react";
import styled from "styled-components";
import ListeningInstructions from "./TestInstructions/ListeningInstructions";
import ReadingInstructions from "./TestInstructions/ReadingInstructions";
import SpeakingInstructions from "./TestInstructions/SpeakingInstructions";
import WritingInstructions from "./TestInstructions/WritingInstructions";
import { useNavigate } from "react-router-dom";

const TestContainer = styled.div`
  min-height: 100vh;
  background: #f0f2f5;
  padding: 0;
  margin: 0;
`;

const TopBar = styled.div`
  background: #003b70;
  padding: 1rem 2rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #1a1a1a;
  font-size: 1.75rem;
  font-weight: 500;
  margin: 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
`;

const SubTitle = styled.p`
  color: #666;
  margin: 1rem 0 0;
  font-size: 1rem;
  line-height: 1.5;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const TestCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const TestCardHeader = styled.div`
  background: ${(props) => props.bgColor};
  color: white;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const TestIcon = styled.div`
  font-size: 2rem;
`;

const TestTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
`;

const TestContent = styled.div`
  padding: 1.5rem;
`;

const TestDescription = styled.p`
  color: #4a4a4a;
  margin: 0 0 1.5rem;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const TestDetails = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1rem 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: #666;
`;

const StartButton = styled.button`
  width: 100%;
  background: #003b70;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #002b53;
  }
`;

const DiagnosticTestScreen = () => {
  const navigate = useNavigate();
  const [selectedTest, setSelectedTest] = useState(null);

  const onBack = () => {
    navigate("/");
  };

  const handleStartTest = (testType) => {
    navigate(`/${testType}-test`);
  };

  if (selectedTest === "listening") {
    return (
      <ListeningInstructions
        onBack={() => setSelectedTest(null)}
        onStart={() => handleStartTest("listening")}
      />
    );
  }

  if (selectedTest === "reading") {
    return (
      <ReadingInstructions
        onBack={() => setSelectedTest(null)}
        onStart={() => handleStartTest("reading")}
      />
    );
  }

  if (selectedTest === "speaking") {
    return (
      <SpeakingInstructions
        onBack={() => setSelectedTest(null)}
        onStart={() => handleStartTest("speaking")}
      />
    );
  }

  if (selectedTest === "writing") {
    return (
      <WritingInstructions
        onBack={() => setSelectedTest(null)}
        onStart={() => handleStartTest("writing")}
      />
    );
  }

  const tests = [
    {
      icon: "📚",
      title: "Reading",
      description:
        "Evaluate your comprehension of academic texts and passages through multiple-choice questions and passage analysis.",
      duration: "54-72 minutes",
      questions: "30-40 questions",
      bgColor: "#2c5282",
    },
    {
      icon: "✍️",
      title: "Writing",
      description:
        "Demonstrate your ability to write clear, well-structured academic essays and responses to prompts.",
      duration: "50 minutes",
      questions: "2 tasks",
      bgColor: "#2f855a",
    },
    {
      icon: "🎧",
      title: "Listening",
      description:
        "Test your ability to understand spoken English from academic settings and lectures.",
      duration: "60-90 minutes",
      questions: "28-39 questions",
      bgColor: "#2b6cb0",
    },
    {
      icon: "🗣️",
      title: "Speaking",
      description:
        "Practice expressing your thoughts clearly in English through various speaking tasks.",
      duration: "17 minutes",
      questions: "4 tasks",
      bgColor: "#975a16",
    },
  ];

  return (
    <TestContainer>
      <TopBar>
        <Logo>TOEFL Practice Test</Logo>
        <div onClick={onBack} style={{ cursor: "pointer" }}>
          Exit Test
        </div>
      </TopBar>
      <MainContent>
        <Header>
          <Title>FREE TOEFL Test Selection</Title>
          <SubTitle>
            Select a section to begin your TOEFL practice test. Each section is
            timed and scored according to official TOEFL standards.
          </SubTitle>
        </Header>
        <TestGrid>
          {tests.map((test, index) => (
            <TestCard key={index}>
              <TestCardHeader bgColor={test.bgColor}>
                <TestIcon>{test.icon}</TestIcon>
                <TestTitle>{test.title}</TestTitle>
              </TestCardHeader>
              <TestContent>
                <TestDescription>{test.description}</TestDescription>
                <TestDetails>
                  <div>⏱ {test.duration}</div>
                  <div>📝 {test.questions}</div>
                </TestDetails>

                {/*  temporaty hiding this until we impliment the flow */}
                {/* <StartButton onClick={() => setSelectedTest(test.title.toLowerCase())}>
                  Start {test.title} Test
                </StartButton> */}
                <StartButton
                  onClick={() => {
                    if (
                      test.title.toLowerCase() === "listening" ||
                      test.title.toLowerCase() === "speaking"
                    ) {
                      window.location.href =
                        "https://calendly.com/amit_patel/book-a-call-with-native-english-speaker";
                    } else {
                      setSelectedTest(test.title.toLowerCase());
                    }
                  }}
                >
                  Start {test.title} Test
                </StartButton>
              </TestContent>
            </TestCard>
          ))}
        </TestGrid>
      </MainContent>
    </TestContainer>
  );
};

export default DiagnosticTestScreen;
